import { useLayoutEffect, useState } from 'react';
import { Controller, useController, useFormContext } from 'react-hook-form';
import { Cell } from '@/shared/ui/Cell';
import Tooltip from '@/shared/ui/Tooltip/Tooltip';
import { classNames } from '@/shared/lib/classNames';
import { DatePicker as DatePickerComponent, DatePickerProps } from '@salutejs/sdds-insol';
import Calendar from '@/shared/assets/icons/calendar.svg?react';
import cls from './DatePicker.module.scss';

const getMinDate = (date: Date | undefined) => {
    if (!date) {
        return undefined;
    }

    const minDate = new Date(date);

    minDate.setDate(minDate.getDate() - 1);

    return minDate;
};

type DatePickerChangeEvent = {
    target: {
        name?: string | undefined;
        value?: string | undefined;
    };
};

type Props = {
    className?: string;
    disabled?: boolean;
    disabledDays?: DatePickerProps['disabledList'];
    initialDate?: string;
    label: string;
    max?: Date;
    min?: Date;
    name: string;
    size?: 'l' | 'm' | 's' | 'xs';
    tooltip?: string;
    onChange?: (value: string | undefined) => void;
};

export const DatePicker = ({
    className = '',
    disabledDays,
    name,
    min,
    max,
    disabled = false,
    label,
    onChange,
    initialDate,
    tooltip,
    size = 'm',
}: Props) => {
    const [isOpened, setIsOpened] = useState(false);

    const { control } = useFormContext();
    const { field } = useController({ name, control });

    useLayoutEffect(() => {
        if (initialDate) {
            field.onChange(initialDate);
        }
    }, []);

    return (
        <Controller
            render={({ field, fieldState }) => (
                <Cell className={classNames(className, { [cls.noPointerEvents]: disabled })} error={fieldState.error?.message}>
                    <DatePickerComponent
                        className="datePicker"
                        contentRight={
                            <div className={cls.datePickerRightContent}>
                                <Calendar />
                                {tooltip && (
                                    <div
                                        onClick={(e) => {
                                            e.stopPropagation();
                                        }}
                                    >
                                        <Tooltip text={tooltip} placement="bottom" />
                                    </div>
                                )}
                            </div>
                        }
                        max={max}
                        name={name}
                        size={size}
                        label={label}
                        opened={isOpened}
                        readOnly={disabled}
                        value={initialDate}
                        min={getMinDate(min)}
                        labelPlacement="inner"
                        disabledList={disabledDays}
                        maskWithFormat
                        includeEdgeDates
                        onFocus={() => {
                            setIsOpened(true);
                        }}
                        onCommitDate={() => {
                            setIsOpened(false);
                        }}
                        onChange={(event: DatePickerChangeEvent) => {
                            field.onChange(event.target.value);
                            onChange?.(event.target.value);
                        }}
                    />
                </Cell>
            )}
            name={name}
        />
    );
};
